import {
  AlertAdapter,
  CommonDropdown,
  CommonInput,
  FormErrorWrap,
  ICommonDropdownOption,
  RegularButton,
} from 'pg-ui-library';
import { AxiosResponse } from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import ReactModal from 'react-modal';
import { useTranslation } from 'react-i18next';
import { CloseIcon } from 'src/modules/login/components/mfa-modal/styles';
import closeSvg from 'src/assets/close.svg';
import PaymentsService from 'src/services/payments.service';
import { QueryObserverResult, useQuery } from '@tanstack/react-query';
import * as yup from 'yup';
import { useFormik } from 'formik';
import {
  AccrualIdConfigurationBody,
  AccrualIdConfigurationFooter,
  AccrualIdConfigurationForm,
  AccrualIdConfigurationHeader,
} from './styles';

export const AccrualPlanModal: React.FC<{
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  refetchAccrualPlans: () => Promise<QueryObserverResult<AxiosResponse<any, any>, unknown>>;
  // eslint-disable-next-line react/prop-types
}> = ({ isOpen, setIsOpen, refetchAccrualPlans }) => {
  const { t } = useTranslation();
  const [dropdownOptionsAccrualIds, setDropdownOptionsAccrualIds] = useState<ICommonDropdownOption[]>([]);
  const [dropdownOptionsCompany, setDropdownOptionsCompany] = useState<ICommonDropdownOption[]>([]);
  const [selectedAccrualId, setSelectedAccrualId] = useState<string>();
  const [selectedCompany, setSelectedCompany] = useState<string>();
  const handleModal = () => {
    setIsOpen(!isOpen);
  };
  const { data: availableAccrualIds } = useQuery(['available'], () => PaymentsService.getAvailableAccrualIds(), {
    enabled: true,
  });

  const validationSchema = yup.object().shape({
    accrualId: yup.string().required(),
    company: yup.string().required(),
    description: yup.string().required().min(1).max(15),
  });

  const formik = useFormik({
    initialValues: {
      accrualId: selectedAccrualId as unknown as '',
      company: selectedCompany as unknown as '',
      description: '',
    },
    validationSchema,
    onSubmit: async values => {
      await PaymentsService.createNewAccrualIdConfig(values);
      AlertAdapter('Accrual Plan Configuration created!', 'success');
      refetchAccrualPlans();
      setIsOpen(!isOpen);
    },
  });

  const mountDropdownAccrualOptions = useCallback(() => {
    const cpnsOptions: ICommonDropdownOption[] = availableAccrualIds?.data?.accrualIds?.map((item: string) => ({
      key: item,
      label: item,
      value: item,
    }));
    cpnsOptions?.unshift({
      key: '',
      label: '',
      value: '',
    });
    setDropdownOptionsAccrualIds(cpnsOptions);
  }, [availableAccrualIds?.data]);

  const mountDropdownCompaniesOptions = useCallback(() => {
    const cpnsOptions: ICommonDropdownOption[] = availableAccrualIds?.data?.companies?.map((item: string) => ({
      key: item,
      label: item,
      value: item,
    }));
    cpnsOptions?.unshift({
      key: '',
      label: '',
      value: '',
    });
    setDropdownOptionsCompany(cpnsOptions);
  }, [availableAccrualIds?.data]);

  useEffect(() => {
    mountDropdownAccrualOptions();
    mountDropdownCompaniesOptions();
  }, [availableAccrualIds]);

  useEffect(() => {
    formik.setFieldValue('accrualId', selectedAccrualId);
    formik.setFieldValue('company', selectedCompany);
  }, [selectedAccrualId, selectedCompany]);

  return (
    // eslint-disable-next-line react/react-in-jsx-scope
    <ReactModal
      isOpen={isOpen}
      ariaHideApp={false}
      onRequestClose={handleModal}
      style={{
        overlay: {
          margin: 'auto',
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
        },
        content: {
          borderRadius: '30px',
          margin: 'auto',
          width: '496px',
          height: '420px',
          display: 'flex',
          flexDirection: 'column',
          padding: '0 40px 0 40px',
          background: '#F3F4F8',
          overflowY: 'hidden',
        },
      }}
    >
      <AccrualIdConfigurationForm onSubmit={formik.handleSubmit}>
        <AccrualIdConfigurationHeader>
          <div>
            <h3>{t('settings.modal.accrualPlan')}</h3>
          </div>
          <div style={{ marginTop: '30px' }}>
            <CloseIcon src={closeSvg} onClick={() => handleModal()} />
          </div>
        </AccrualIdConfigurationHeader>
        <AccrualIdConfigurationBody>
          <div style={{ display: 'flex' }}>
            <div>
              <CommonDropdown
                label="Accrual Id"
                id="accrualId"
                name="accrualId"
                options={dropdownOptionsAccrualIds || []}
                wrapperStyle={{ width: '185px', marginRight: '13px' }}
                selectStyle={{ maxWidth: 'none', width: '100%' }}
                setValue={setSelectedAccrualId}
              />
              <FormErrorWrap visible errorMessage={formik.errors.accrualId || ''} />
            </div>
            <div>
              <CommonDropdown
                label="Company"
                id="company"
                name="company"
                options={dropdownOptionsCompany || []}
                wrapperStyle={{ width: '185px', marginRight: '13px' }}
                selectStyle={{ maxWidth: 'none', width: '100%' }}
                setValue={setSelectedCompany}
              />
              <FormErrorWrap visible errorMessage={formik.errors.company || ''} />
            </div>
          </div>
          <div>
            <CommonInput
              id="description"
              name="description"
              label="Description"
              placeholder="Accrual Id Description"
              style={{ width: '380px' }}
              onChange={e => formik.setFieldValue('description', e.target.value)}
            />
            <FormErrorWrap visible errorMessage={formik.errors.description || ''} />
          </div>
          <AccrualIdConfigurationFooter>
            <RegularButton disabled={formik.isSubmitting || !formik.isValid} type="submit">
              {t('settings.modal.button')}
            </RegularButton>
          </AccrualIdConfigurationFooter>
        </AccrualIdConfigurationBody>
      </AccrualIdConfigurationForm>
    </ReactModal>
  );
};
